import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatRadioModule } from '@angular/material/radio';
import { ActivatedRoute, Router } from '@angular/router';
import { TagsLanguageService } from '../../features/tags-language/tagsLanguage.service';
import { ToastrService } from 'ngx-toastr';
import { ContentLoaderComponent } from '../../shared/components/content-loader-component/contentLoader.component';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  noOnlySpacesValidator,
  noOnlySpecialCharsValidator,
} from '../../core/helpers/validator';

@Component({
  selector: 'app-new-tags',
  imports: [
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    MatSelectModule,
    MatRadioModule,
    CommonModule,
    ContentLoaderComponent,
  ],
  templateUrl: './new-tags.component.html',
  styleUrl: './new-tags.component.scss',
})
export class NewTagsComponent {
  public languages: any[] = [];
  public form: FormGroup;
  public tagTranslations: {
    langId: number;
    tagName: string;
  }[] = [];
  tagId: any;
  selected: any;
  updated: boolean = false;
  isEditing: boolean = false;
  loading: boolean = false;
  isSubmitting = false;
  payload: {
    langId: number;
    tagName: string;
  }[] = [];

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private tagsService: TagsLanguageService,
    private toastr: ToastrService,
    private route: ActivatedRoute
  ) {
    this.form = this.fb.group({
      name: [
        '',
        [
          Validators.required,
          Validators.maxLength(50),
          noOnlySpecialCharsValidator(),
          noOnlySpacesValidator(),
        ],
      ],
      langId: [null, Validators.required],
      type: ['coloring', Validators.required],
    });
  }

  ngOnInit() {
    this.getLanguages();
    this.route.paramMap.subscribe((params) => {
      this.tagId = params.get('id');
      if (this.tagId) {
        this.loading = true;
        if (this.tagTranslations.length === this.languages.length) {
          this.form.get('name')?.disable();
          this.form.get('langId')?.disable();
        }
        this.getTagById(this.tagId);
      } else {
        this.form.get('langId')?.patchValue(1);
      }
    });
  }

  getLanguages(): void {
    this.tagsService.getLangauges().subscribe({
      next: (res) => {
        if (res?.data) {
          this.languages = res?.data;
        }
      },
      error: (err) => {
        console.log(err);
      },
    });
  }

  getTagById(id: any) {
    this.tagsService.getTagById(id).subscribe({
      next: (res: any) => {
        this.getLanguages();
        const tagsData = (res as any).data;
        if (tagsData.type) {
          this.form.get('type')?.patchValue(tagsData.type);
        }
        tagsData.tagTranslation.forEach((translation: any) => {
          this.tagTranslations.push({
            langId: translation.langId,
            tagName: translation.tagName,
          });
        });
        this.loading = false;
      },
      error: (err) => {
        this.loading = false;
      },
    });
  }

  getLanguageName(langId: number): string {
    const lang = this.languages.find((l) => l.id === langId);
    return lang ? lang.name : `Lang ID: ${langId}`;
  }

  get sortedTranslations() {
    return this.tagTranslations
      ?.slice()
      .sort((a, b) => (a.langId === 1 ? -1 : b.langId === 1 ? 1 : 0));
  }

  isLangSelected(langId: number): boolean {
    return this.tagTranslations.some((t) => t.langId === langId);
  }

  addTag(): void {
    const { name, langId } = this.form.value;
    if (this.form.invalid) {
      this.toastr.warning('Please enter a valid tag and select a language');
      return;
    }
    const isDuplicate = this.tagTranslations.some((t) => t.langId === langId);
    if (isDuplicate) {
      this.toastr.error('This language is already added');
      return;
    }
    this.tagTranslations.push({ tagName: name, langId });
    this.form.reset({ type: this.form.getRawValue().type });
    if (this.tagTranslations.length === this.languages.length) {
      this.form.get('name')?.disable();
      this.form.get('langId')?.disable();
    }
    if (this.selected.tagName != this.form.value.name) {
      this.updated = true;
      const index = this.payload.findIndex(
        (payload) => payload.langId === langId
      );
      if (index !== -1) {
        this.payload[index] = { tagName: name, langId };
      } else {
        this.payload.push({ tagName: name, langId });
      }
    }
    this.form.reset({ type: this.form.getRawValue().type });
    this.isEditing = false;
  }

  removeTag(index: number): void {
    this.tagTranslations.splice(index, 1);
    if (this.tagTranslations.length !== 5) {
      this.form.get('name')?.enable();
      this.form.get('langId')?.enable();
    }
  }

  editTag(index: number): void {
    if (this.isEditing) {
      this.addTag();
    }
    this.isEditing = true;
    this.form.get('name')?.enable();
    this.form.get('langId')?.enable();
    this.selected = this.tagTranslations[index];
    this.tagTranslations.splice(index, 1);
    this.form.patchValue({
      name: this.selected.tagName,
      langId: this.selected.langId,
    });
    this.scrollUp(`translation`);
  }

  scrollUp(elementId: string): void {
    const element = document.getElementById(elementId);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }

  onCancel() {
    this.router.navigate(['/tags']);
  }

  saveTags(): void {
    const type = this.form.getRawValue().type;
    const createPayload = { type, translations: this.tagTranslations };
    const updatePayload = {
      type,
      translations: this.updated ? this.payload : this.tagTranslations,
    };
    this.isSubmitting = true;
    if (this.tagId) {
      this.tagsService.updateTag(this.tagId, updatePayload).subscribe({
        next: (res) => {
          this.toastr.success('Tags updated successfully');
          this.form.reset({ type: this.form.getRawValue().type });
          this.isSubmitting = false;
          this.router.navigate(['/tags']);
          return;
        },
        error: (err) => {
          this.isSubmitting = false;
          this.form.reset({ type: this.form.getRawValue().type });
        },
      });
    } else {
      this.tagsService.createTag(createPayload).subscribe({
        next: (res) => {
          this.toastr.success('Tags created successfully');
          this.isSubmitting = false;
          this.form.reset({ type: this.form.getRawValue().type });
          this.router.navigate(['/tags']);
          return;
        },
        error: (err) => {
          this.isSubmitting = false;
          this.form.reset({ type: this.form.getRawValue().type });
        },
      });
    }
  }
}
